import { enumMembers, type ApiSchema } from "../../apiSchema";
import { readObject, readOneOf, readString, readStringRecord, type JsonObject } from "../../http/jsonDecoding";

/** ADR-0032 D8: keep unedited members, including explicit nulls, after checking their shape. */
// Infer the kept members from their readers, rather than the enclosing content's return type.
export function keptMembers<T>(content: JsonObject, readers: { [K in keyof T]: (value: unknown) => T[K] }): NoInfer<{ [K in keyof T]?: T[K] | null }> {
  const kept: { [K in keyof T]?: T[K] | null } = {};
  for (const member in readers) {
    const value = content[member];
    if (value !== undefined) kept[member] = value === null ? null : readers[member](value);
  }
  return kept;
}

export const PROCESS_MEMBERS = {
  lifecycle: decodeLifecycle,
  kind_of: (value: unknown) => readString({ kind_of: value }, "kind_of"),
};

type Translation = ApiSchema<"Translation">;

/** Every optional member of a translation, read so that saving keeps it (ADR-0032 D8); a new one fails the typecheck here. */
const TRANSLATION_KEPT_READERS = {
  instructions: (instructions: unknown) => readString({ instructions }, "instructions"),
  choice_labels: (choice_labels: unknown) => readStringRecord({ choice_labels }, "choice_labels"),
  input_labels: (input_labels: unknown) => readStringRecord({ input_labels }, "input_labels"),
  // A Workflow's abstract steps' label and description (ADR-0043 D1).
  abstract_steps: (steps: unknown) => Object.fromEntries(Object.entries(readObject(steps, "abstract steps")).map(([key, text]) => {
    const words = readObject(text, "abstract step text");
    return [key, { label: readString(words, "label"), description: readString(words, "description") }];
  })),
} satisfies { readonly [K in Exclude<keyof Translation, "name" | "description">]-?: (value: unknown) => NonNullable<Translation[K]> };

/** P-21: the same optional, lossless translation shape on Workflows and steps. */
export function decodeTranslations(value: unknown): Record<string, Translation> {
  return Object.fromEntries(Object.entries(readObject(value, "translations")).map(([language, translated]) => {
    const translation = readObject(translated, "translation");
    return [language, { name: readString(translation, "name"), description: readString(translation, "description"), ...keptMembers(translation, TRANSLATION_KEPT_READERS) }];
  }));
}

const LIFECYCLE_STATES = enumMembers<ApiSchema<"LifecycleState">>({ draft: true, active: true, deprecated: true, retired: true, withdrawn: true });

function decodeLifecycle(value: unknown): ApiSchema<"ItemLifecycle"> {
  const lifecycle = readObject(value, "lifecycle");
  return {
    state: readOneOf(lifecycle, "state", LIFECYCLE_STATES),
    ...keptMembers(lifecycle, {
      reason: (reason) => readString({ reason }, "reason"),
      replaced_by: (replacement) => readString({ replaced_by: replacement }, "replaced_by"),
    }),
  };
}

